export const validateProjectForm = (projectInfo) => {
	const errors = {};
	if (!projectInfo.projectName) errors.projectName = 'Project name is required';
	if (!projectInfo.projectType?.value) errors.projectType = 'Project type is required';
	if (!projectInfo.projectOwner?.value) errors.projectOwner = 'Project owner is required';
	if (!projectInfo.brand?.value) errors.brand = 'Brand is required';
	if (!projectInfo.priority?.value) errors.priority = 'Priority is required';

	if (projectInfo.projectType?.id !== 'BUYER_SIGN_OFF') {
		if (!projectInfo.projectStartDate) errors.projectStartDate = 'Project start date is required';
		if (!projectInfo.projectEndDate) errors.projectEndDate = 'Project end date is required';

		if (projectInfo.projectStartDate && projectInfo.projectEndDate) {
			const startDate = new Date(projectInfo.projectStartDate);
			const endDate = new Date(projectInfo.projectEndDate);
			if (endDate < startDate) {
				errors.projectEndDate = 'Project end date cannot be earlier than start date';
			}
		}
	} else {
		if (!projectInfo.buyerSignOffDate) errors.buyerSignOffDate = 'Buyer sign off date is required';
	}

	// Only validate these fields if project type is not GENERAL
	if (
		projectInfo.projectType?.id !== 'GENERAL' &&
		projectInfo.projectType?.id !== 'BUYER_SIGN_OFF' &&
		!isProjectTypeHasOnboarding(projectInfo.projectType?.id)
	) {
		if (!projectInfo.buyer1stMeetingDate) {
			errors.buyer1stMeetingDate = 'Buyer 1st meeting date is required';
		} else {
			// Validate buyer meeting date is within project date range
			const startDate = new Date(projectInfo.projectStartDate);
			const endDate = new Date(projectInfo.projectEndDate);
			const meetingDate = new Date(projectInfo.buyer1stMeetingDate);

			if (meetingDate < startDate || meetingDate > endDate) {
				errors.buyer1stMeetingDate =
					'Buyer 1st meeting date must be between project start and end dates';
			}
		}
		if (!projectInfo.season) errors.season = 'Season is required';
	}

	return errors;
};
export const isGeneralProjectType = (type) => {
	return type === 'GENERAL' || !type;
};

export const isProjectTypeBuyerSignOff = (type) => {
	return type === 'BUYER_SIGN_OFF' || !type;
};

export const isProjectTypeHasOnboarding = (type) => {
	return ['ONBOARDING_NEW_CUSTOMER', 'ONBOARDING_NEW_DEPARTMENT', 'OPENING_NEW_CORE_PROGRAM'].includes(
		type
	);
};

export const getWhichProjectToRefetch = ({
	projectStatus,
	refetchNotStartedProjects,
	refetchInProgressProjects,
	refetchCompletedProjects
}) => {
	if (projectStatus === 'PENDING') {
		return refetchNotStartedProjects;
	} else if (projectStatus === 'RUNNING') {
		return refetchInProgressProjects;
	} else if (projectStatus === 'COMPLETED') {
		return refetchCompletedProjects;
	}
};

export const getDaysCount = (startDate, endDate) => {
	const start = new Date(startDate);
	const end = new Date(endDate);
	const timeDiff = Math.abs(end.getTime() - start.getTime());
	const diffDays = Math.ceil(timeDiff / (1000 * 3600 * 24));
	return diffDays;
};

export const handleOpenLink = (url) => {
	const newUrl = url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`;
	window.open(newUrl, '_blank');
};

export const sortWithUniqueOrder = (itemsToSort) => {
	if (!itemsToSort || !itemsToSort.length) return [];

	// Sort projects by sl and id
	return [...itemsToSort].sort((a, b) => {
		if (a.sl === b.sl) return a.id - b.id;
		return a.sl - b.sl;
	});
};

const DEFAULT_START_SL = 1000;
const MIN_DELTA = 1;

export const calculateNewSl = (tasks, overIndex) => {
	if (tasks.length === 0) return DEFAULT_START_SL;

	// Find lowest and highest SL values
	const lowestSl = Math.min(...tasks.map((task) => task.sl));
	const highestSl = Math.max(...tasks.map((task) => task.sl));

	if (overIndex === 0) {
		// If dropped at the top lowest SL - 1
		return lowestSl - MIN_DELTA;
	} else if (overIndex >= tasks.length) {
		return highestSl + MIN_DELTA;
	} else {
		const prevSl = tasks[overIndex - 1].sl;
		const nextSl = tasks[overIndex].sl;

		if (prevSl === nextSl) {
			const allSls = tasks.map((task) => task.sl).sort((a, b) => a - b);

			for (let i = 0; i < allSls.length - 1; i++) {
				if (allSls[i + 1] - allSls[i] > MIN_DELTA) {
					return allSls[i] + MIN_DELTA;
				}
			}
			return highestSl + MIN_DELTA;
		}

		// Calculate the midpoint between prevSl and nextSl
		const mid = prevSl + (nextSl - prevSl) / 2;

		if (nextSl - prevSl <= MIN_DELTA) {
			const newSl = prevSl + MIN_DELTA;
			if (tasks.some((task) => task.sl === newSl)) {
				return highestSl + MIN_DELTA;
			}
			return newSl;
		}

		return mid;
	}
};

export const getOwnerList = (taskList = []) => {
	const uniqueOwners = {};

	[...taskList]?.forEach((task) => {
		if (task?.ownerResponse?.name) {
			const ownerId = task.ownerResponse.id;
			uniqueOwners[ownerId] = {
				id: ownerId,
				name: task.ownerResponse.name
			};
		}
	});

	return Object.values(uniqueOwners);
};

export const getStatusList = (taskList = []) => {
	const uniqueStatuses = {};

	[...taskList]?.forEach((task) => {
		if (task?.taskStatusTagResponse?.text) {
			const statusId = task.taskStatusTagResponse.id;
			uniqueStatuses[statusId] = {
				id: statusId,
				name: task.taskStatusTagResponse.text,
				constant: task.taskStatusTagResponse.constant
			};
		}
	});

	return Object.values(uniqueStatuses);
};

export const isHideColumn = (columnId, selectedColumnToHide) => {
	return !selectedColumnToHide.includes(columnId);
};

export const isValidRequestBody = (body, onResetState) => {
	const fieldsToValidate = ['name', 'startDate', 'dueDate', 'remarks'];

	const hasEmptyRequiredField = fieldsToValidate.some(
		(field) => field in body && (!body[field] || body[field].trim() === '')
	);

	const hasInvalidOwner = 'ownerId' in body && !body.ownerId;

	if (hasEmptyRequiredField || hasInvalidOwner) {
		onResetState && onResetState();
		return false;
	}

	return true;
};

export const getRandomColor = (char) => {
	const colorMap = {
		A: '#FF5733',
		B: '#33FF57',
		C: '#3357FF',
		D: '#F3FF33',
		E: '#FF33F3',
		F: '#33FFF3',
		G: '#FF8C33',
		H: '#8C33FF',
		I: '#33FF8C',
		J: '#FF3333',
		K: '#33FF33',
		L: '#3333FF',
		M: '#FFFF33',
		N: '#FF33FF',
		O: '#33FFFF',
		P: '#FF5733',
		Q: '#33FF57',
		R: '#3357FF',
		S: '#F3FF33',
		T: '#FF33F3',
		U: '#33FFF3',
		V: '#FF8C33',
		W: '#8C33FF',
		X: '#33FF8C',
		Y: '#FF3333',
		Z: '#33FF33'
	};

	if (char && colorMap[char.toUpperCase()]) {
		return colorMap[char.toUpperCase()];
	}

	return `#${Math.floor(Math.random() * 16777215).toString(16)}`;
};

export const isProjectOwner = (ownerId, loggedInUserId) => {
	return ownerId === loggedInUserId;
};

export const isProjectMember = (memberId, loggedInUserId) => {
	return memberId === loggedInUserId;
};

export const createFilterRequestBody = (filterfields) => {
	const fields = {};
	for (const property in filterfields) {
		if (property === 'ownerList') {
			fields.ownerIdList = filterfields[property]?.map((owner) => owner?.key);
		}
		if (property === 'projectType') {
			fields.projectTypeList = filterfields[property]?.map((projectType) => projectType?.key);
		}
		if (property === 'brandIds') {
			fields.brandIdList = filterfields[property]?.map((projectType) => projectType?.key);
		}
		if (property === 'projectDueDate') {
			const [start, end] = filterfields[property][0]?.key?.split(',') || [];
			fields.dueDateStart = start && start !== 'null' ? start : null;
			fields.dueDateEnd = end && end !== 'null' ? end : null;
		}
	}

	return fields;
};

export const handleParseJson = (jsonResponse) => {
	try {
		const parsed = JSON.parse(jsonResponse);
		if (Array.isArray(parsed)) return parsed;
		else return [];
	} catch {
		return [];
	}
};

export const renameStatus = (status) => {
	if (!status) return '';
	switch (status) {
		case 'PENDING':
			return 'Not Started';
		case 'RUNNING':
			return 'Running';
		case 'COMPLETED':
			return 'Completed';
		default:
			return status;
	}
};
